export function createCalculateTax({
  personalAllowance,
  basicRateThreshold,
  higherRateThreshold,
}) {
  return function calculateTax(income) {
    if (income <= personalAllowance) return 0;
    let tax = 0;
    let remaining = income - personalAllowance;
    const basicRateBand = basicRateThreshold - personalAllowance;
    if (remaining > 0) {
      const taxableAtBasic = Math.min(remaining, basicRateBand);
      tax += taxableAtBasic * 0.20;
      remaining -= taxableAtBasic;
    }
    const higherRateBand = higherRateThreshold - basicRateThreshold;
    if (remaining > 0) {
      const taxableAtHigher = Math.min(remaining, higherRateBand);
      tax += taxableAtHigher * 0.40;
      remaining -= taxableAtHigher;
    }
    if (remaining > 0) {
      tax += remaining * 0.45;
    }
    return tax;
  };
}

export function annuityGrossForAge({
  age,
  retirementAge,
  lifeExpectancy,
  annuityAnnualGross = 0,
}) {
  if (annuityAnnualGross <= 0) return 0;
  if (age < retirementAge || age > lifeExpectancy) return 0;
  return annuityAnnualGross;
}

export function dbPensionGrossForAge({
  age,
  dbPensionAge,
  dbPensionAnnual = 0,
  lifeExpectancy,
}) {
  if (dbPensionAnnual <= 0) return 0;
  if (age < dbPensionAge || age > lifeExpectancy) return 0;
  return dbPensionAnnual;
}

export function computeAnnuityPurchase({
  annuityEnabled,
  annuityPurchasePercent,
  postLumpPot,
}) {
  if (!annuityEnabled || postLumpPot <= 0) return 0;
  const pct = Math.max(0, Math.min(100, annuityPurchasePercent));
  return postLumpPot * (pct / 100);
}

/** Exact £ purchase when set; otherwise percent × post-lump pot. */
export function resolveAnnuityPurchase({
  annuityEnabled,
  annuityPurchasePercent,
  annuityPurchaseAmountExact = null,
  postLumpPot,
}) {
  if (!annuityEnabled || postLumpPot <= 0) return 0;
  if (
    annuityPurchaseAmountExact != null
    && !Number.isNaN(annuityPurchaseAmountExact)
  ) {
    return Math.max(0, Math.min(annuityPurchaseAmountExact, postLumpPot));
  }
  return computeAnnuityPurchase({
    annuityEnabled,
    annuityPurchasePercent,
    postLumpPot,
  });
}

export function annuityPurchasePercentFromAmount(purchase, postLumpPot) {
  if (postLumpPot <= 0) return 0;
  return Math.min(100, Math.max(0, (purchase / postLumpPot) * 100));
}

export function formatAnnuityPurchasePercent(pct) {
  if (pct >= 99.95) return '100';
  if (pct <= 0.05) return '0';
  const rounded = Math.round(pct * 100) / 100;
  if (Math.abs(rounded - Math.round(rounded)) < 0.001) {
    return String(Math.round(rounded));
  }
  if (Math.abs(rounded * 10 - Math.round(rounded * 10)) < 0.001) {
    return rounded.toFixed(1);
  }
  return rounded.toFixed(2);
}

export const ANNUITY_INCOME_RATE_MIN = 2;
export const ANNUITY_INCOME_RATE_MAX = 10;

export function clampAnnuityIncomeRatePercent(rate) {
  return Math.min(ANNUITY_INCOME_RATE_MAX, Math.max(ANNUITY_INCOME_RATE_MIN, rate));
}

export function computeAnnuityAnnualGross(annuityPurchase, annuityIncomeRatePercent) {
  if (annuityPurchase <= 0) return 0;
  return annuityPurchase * (clampAnnuityIncomeRatePercent(annuityIncomeRatePercent) / 100);
}

export function annuityIncomeRateFromGross(annualGross, annuityPurchase) {
  if (annuityPurchase <= 0) return ANNUITY_INCOME_RATE_MIN;
  const rate = (annualGross / annuityPurchase) * 100;
  return clampAnnuityIncomeRatePercent(rate);
}

/** Income rate % implied by annual gross and purchase (2–10% band). */
export const annuityIncomeRatePercentFromGross = annuityIncomeRateFromGross;

/** Exact £/yr income when set; otherwise purchase × income rate %. */
export function resolveAnnuityAnnualGross({
  annuityEnabled,
  annuityPurchase,
  annuityIncomeRatePercent,
  annuityAnnualGrossExact = null,
}) {
  if (!annuityEnabled || annuityPurchase <= 0) return 0;
  const minGross = annuityPurchase * (ANNUITY_INCOME_RATE_MIN / 100);
  const maxGross = annuityPurchase * (ANNUITY_INCOME_RATE_MAX / 100);
  if (
    annuityAnnualGrossExact != null
    && !Number.isNaN(annuityAnnualGrossExact)
  ) {
    return Math.max(minGross, Math.min(maxGross, annuityAnnualGrossExact));
  }
  return computeAnnuityAnnualGross(annuityPurchase, annuityIncomeRatePercent);
}

export const formatAnnuityIncomeRatePercent = formatAnnuityPurchasePercent;

/** Grow buffer balance, then withdraw an equal share of remaining years (incl. growth). */
export function computeCashBufferWithdrawal({
  balance,
  nominalGrowthRate,
  yearsRemaining,
}) {
  if (balance <= 0 || yearsRemaining <= 0) {
    return { draw: 0, balanceAfter: Math.max(0, balance) };
  }
  const balanceAfterGrowth = balance * (1 + nominalGrowthRate);
  const draw = balanceAfterGrowth / yearsRemaining;
  return {
    draw,
    balanceAfter: Math.max(0, balanceAfterGrowth - draw),
  };
}

/** One retirement year: grow then draw from cash buffer. */
export function projectCashBufferYear({
  balance,
  nominalGrowthRate,
  yearsRemaining,
}) {
  return computeCashBufferWithdrawal({ balance, nominalGrowthRate, yearsRemaining });
}

/** Target net income for a plan year (inflation-linked from retirement if enabled). */
export function targetNetAtAge({
  baseTarget,
  retirementAge,
  age,
  inflationRateDecimal,
  inflateWithdrawals,
}) {
  if (!inflateWithdrawals || age === retirementAge) return baseTarget;
  const yearsIntoRetirement = age - retirementAge;
  return baseTarget * Math.pow(1 + inflationRateDecimal, yearsIntoRetirement);
}

/**
 * Pre-retirement: pay up to target net from buffer (base target, not inflation-linked).
 * Post-retirement: equal depletion over spread, capped at base target (tax-free slice toward target).
 * Callers pass the fixed cash-buffer target; pot drawdown may use a higher inflation-linked target separately.
 * nominalGrowthRate is typically 0 (cash/ISA does not compound in the app yet).
 */
export function computeCashBufferDrawForYear({
  balance,
  nominalGrowthRate,
  targetNet,
  age,
  retirementAge,
  spreadEndAge,
}) {
  if (balance <= 0) {
    return { draw: 0, balanceAfter: 0 };
  }
  // Cash/ISA buffer: no compounding (nominalGrowthRate kept for API compatibility only).
  void nominalGrowthRate;
  const balanceAfterGrowth = balance;
  if (targetNet <= 0) {
    return { draw: 0, balanceAfter: balanceAfterGrowth };
  }
  if (age < retirementAge) {
    const draw = Math.min(balanceAfterGrowth, targetNet);
    return { draw, balanceAfter: Math.max(0, balanceAfterGrowth - draw) };
  }
  const yearsRemaining = spreadEndAge - age + 1;
  if (yearsRemaining <= 0) {
    return { draw: 0, balanceAfter: balanceAfterGrowth };
  }
  const mechanicalDraw = balanceAfterGrowth / yearsRemaining;
  const draw = Math.min(mechanicalDraw, targetNet);
  return { draw, balanceAfter: Math.max(0, balanceAfterGrowth - draw) };
}

/** Simulate buffer from spread start through spread end; pre-ret years must meet full target. */
export function simulateCashBufferSpread({
  initialBalance,
  spreadStartAge,
  spreadEndAge,
  retirementAge,
  getTargetNetForAge,
  nominalGrowthRate,
}) {
  let balance = initialBalance;
  for (let age = spreadStartAge; age <= spreadEndAge; age++) {
    const targetNet = getTargetNetForAge(age);
    const { draw, balanceAfter } = computeCashBufferDrawForYear({
      balance,
      nominalGrowthRate,
      targetNet,
      age,
      retirementAge,
      spreadEndAge,
    });
    if (age < retirementAge && targetNet > 0 && draw < targetNet - 0.01) {
      return { feasible: false, endingBalance: balanceAfter };
    }
    balance = balanceAfter;
  }
  return { feasible: true, endingBalance: balance };
}

/** Max calendar years before retirement the buffer may start (earliest use = currentAge + 1). */
export function cashBufferPreRetirementLookbackCap(currentAge, retirementAge) {
  return Math.max(0, retirementAge - currentAge - 1);
}

/** Max years before retirement the buffer can fund target through full spread. */
export function maxPreRetirementBridgeYears({
  initialBalance,
  retirementAge,
  currentAge,
  spreadEndAge,
  getTargetNetForAge,
  nominalGrowthRate,
  maxLookback = cashBufferPreRetirementLookbackCap(currentAge, retirementAge),
}) {
  if (initialBalance <= 0) return 0;
  const cap = Math.min(
    maxLookback,
    Math.max(0, retirementAge - currentAge),
    cashBufferPreRetirementLookbackCap(currentAge, retirementAge),
  );
  let maxYears = 0;
  for (let n = 0; n <= cap; n++) {
    const spreadStartAge = retirementAge - n;
    const { feasible } = simulateCashBufferSpread({
      initialBalance,
      spreadStartAge,
      spreadEndAge,
      retirementAge,
      getTargetNetForAge,
      nominalGrowthRate,
    });
    if (feasible) maxYears = n;
    else break;
  }
  return maxYears;
}

/**
 * First age at which the cash buffer balance hits zero after having been positive.
 * Uses chart row `cashBufferBalance` (balance after the year's draw).
 */
export function cashBufferDepletedAgeFromChartData(
  data,
  { spreadStart = null, spreadEnd = null } = {},
) {
  if (!data?.length) return null;
  let hadPositiveBalance = false;
  for (const row of data) {
    if (row.cashBufferBalance == null) continue;
    if (spreadStart != null && row.age < spreadStart) continue;
    if (spreadEnd != null && row.age > spreadEnd) continue;
    if (row.cashBufferBalance > 0) {
      hadPositiveBalance = true;
    } else if (hadPositiveBalance) {
      return row.age;
    }
  }
  return null;
}

/** Buffer balance at the start of `targetAge` after draws from spread start. */
export function cashBufferBalanceAtAge({
  initialBalance,
  spreadStartAge,
  targetAge,
  retirementAge,
  spreadEndAge,
  getTargetNetForAge,
  nominalGrowthRate,
}) {
  let balance = initialBalance;
  for (let age = spreadStartAge; age < targetAge; age++) {
    if (age > spreadEndAge) break;
    const { balanceAfter } = computeCashBufferDrawForYear({
      balance,
      nominalGrowthRate,
      targetNet: getTargetNetForAge(age),
      age,
      retirementAge,
      spreadEndAge,
    });
    balance = balanceAfter;
  }
  return balance;
}

export function statePensionGrossForAge({
  age,
  statePensionAge,
  partnerStatePensionAge = statePensionAge,
  includeStatePensionInWithdrawal = true,
  statePensionAnnual,
  partnerStatePensionAnnual = 0,
  includePartnerStatePension = false,
}) {
  if (!includeStatePensionInWithdrawal) return 0;
  let total = 0;
  if (age >= statePensionAge) total += statePensionAnnual;
  if (includePartnerStatePension && partnerStatePensionAnnual > 0 && age >= partnerStatePensionAge) {
    total += partnerStatePensionAnnual;
  }
  return total;
}

export function computeFinancesForPotWithdrawal({
  potWithdrawal,
  statePensionAnnual,
  partnerStatePensionAnnual = 0,
  includePartnerStatePension = false,
  annuityAnnual = 0,
  dbPensionAnnual = 0,
  dbPensionAge = 65,
  lifeExpectancy = 120,
  age,
  statePensionAge,
  partnerStatePensionAge = statePensionAge,
  includeStatePensionInWithdrawal = true,
  taxFreeOption,
  cumulativeTaxFreeWithdrawn,
  taxFreeLumpSumCap,
  calculateTax,
  lumpSumThisYear = 0,
  cashBufferAnnual = 0,
}) {
  const statePensionTaxable = includeStatePensionInWithdrawal && age >= statePensionAge
    ? statePensionAnnual
    : 0;
  const partnerStatePension = includeStatePensionInWithdrawal
    && includePartnerStatePension
    && age >= partnerStatePensionAge
    ? partnerStatePensionAnnual
    : 0;
  const dbPension = dbPensionGrossForAge({
    age,
    dbPensionAge,
    dbPensionAnnual,
    lifeExpectancy,
  });
  let taxFreeAmount = 0;
  let taxableIncome;

  if (taxFreeOption === 'phased') {
    const potentialTaxFree = potWithdrawal * 0.25;
    const remainingAllowance = Math.max(0, taxFreeLumpSumCap - cumulativeTaxFreeWithdrawn);
    taxFreeAmount = Math.round(Math.max(0, Math.min(potentialTaxFree, remainingAllowance)) * 100) / 100;
    const taxableWithdrawal = potWithdrawal - taxFreeAmount;
    taxableIncome = taxableWithdrawal + statePensionTaxable + dbPension + annuityAnnual;
  } else {
    taxableIncome = potWithdrawal + statePensionTaxable + dbPension + annuityAnnual;
  }

  const taxPaid = calculateTax(taxableIncome);
  const recurringNet = potWithdrawal + statePensionTaxable + partnerStatePension + dbPension + annuityAnnual - taxPaid;
  const displayNet = recurringNet + lumpSumThisYear + cashBufferAnnual;
  const potOnlyNet = potWithdrawal - taxPaid;

  const taxFreeTakenThisYear = taxFreeAmount + lumpSumThisYear;

  return {
    grossPotWithdrawal: potWithdrawal,
    taxFreeAmount,
    taxPaid,
    taxableIncome,
    recurringNet,
    displayNet,
    potOnlyNet,
    updatedCumulativeTaxFreeWithdrawn:
      Math.round((cumulativeTaxFreeWithdrawn + taxFreeTakenThisYear) * 100) / 100,
  };
}

export function solvePotWithdrawalForNetTarget({
  targetNet,
  age,
  statePensionAge,
  partnerStatePensionAge = statePensionAge,
  statePensionAnnual,
  partnerStatePensionAnnual = 0,
  includePartnerStatePension = false,
  annuityAnnual = 0,
  dbPensionAnnual = 0,
  dbPensionAge = 65,
  lifeExpectancy = 120,
  includeStatePensionInWithdrawal,
  taxFreeOption,
  cumulativeTaxFreeWithdrawn,
  taxFreeLumpSumCap,
  calculateTax,
  cashBufferAnnual = 0,
}) {
  const effectiveTarget = Math.max(0, targetNet - cashBufferAnnual);

  const baseParams = {
    statePensionAnnual,
    partnerStatePensionAnnual,
    includePartnerStatePension,
    annuityAnnual,
    dbPensionAnnual,
    dbPensionAge,
    lifeExpectancy,
    age,
    statePensionAge,
    partnerStatePensionAge,
    includeStatePensionInWithdrawal,
    taxFreeOption,
    cumulativeTaxFreeWithdrawn,
    taxFreeLumpSumCap,
    calculateTax,
    lumpSumThisYear: 0,
    cashBufferAnnual,
  };

  if (effectiveTarget <= 0) {
    return computeFinancesForPotWithdrawal({ ...baseParams, potWithdrawal: 0 });
  }

  const dbPensionThisYear = dbPensionGrossForAge({ age, dbPensionAge, dbPensionAnnual, lifeExpectancy });
  const countsTowardNetTarget = includeStatePensionInWithdrawal || dbPensionThisYear > 0;
  const netForTarget = (potWithdrawal) => {
    const finances = computeFinancesForPotWithdrawal({ ...baseParams, potWithdrawal });
    return countsTowardNetTarget ? finances.recurringNet : finances.potOnlyNet;
  };

  const statePensionThisYear = includeStatePensionInWithdrawal && age >= statePensionAge
    ? statePensionAnnual
    : 0;
  const partnerStatePensionThisYear = includeStatePensionInWithdrawal
    && includePartnerStatePension
    && age >= partnerStatePensionAge
    ? partnerStatePensionAnnual
    : 0;
  const guaranteedIncome = statePensionThisYear + partnerStatePensionThisYear
    + dbPensionThisYear + annuityAnnual + cashBufferAnnual;

  let low = 0;
  let high = Math.max(effectiveTarget * 2, effectiveTarget + guaranteedIncome + 10000);

  while (netForTarget(high) < effectiveTarget - 1 && high < effectiveTarget * 20) {
    high *= 2;
  }

  for (let attempt = 0; attempt < 30; attempt++) {
    const mid = (low + high) / 2;
    if (netForTarget(mid) < effectiveTarget) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return computeFinancesForPotWithdrawal({ ...baseParams, potWithdrawal: high });
}

export function grossToNetTarget({
  grossPotWithdrawal,
  age,
  statePensionAge,
  partnerStatePensionAge = statePensionAge,
  statePensionAnnual,
  partnerStatePensionAnnual = 0,
  includePartnerStatePension = false,
  annuityAnnual = 0,
  dbPensionAnnual = 0,
  dbPensionAge = 65,
  lifeExpectancy = 120,
  includeStatePensionInWithdrawal,
  taxFreeOption,
  cumulativeTaxFreeWithdrawn,
  taxFreeLumpSumCap,
  calculateTax,
}) {
  const finances = computeFinancesForPotWithdrawal({
    potWithdrawal: grossPotWithdrawal,
    statePensionAnnual,
    partnerStatePensionAnnual,
    includePartnerStatePension,
    annuityAnnual,
    dbPensionAnnual,
    dbPensionAge,
    lifeExpectancy,
    age,
    statePensionAge,
    partnerStatePensionAge,
    includeStatePensionInWithdrawal,
    taxFreeOption,
    cumulativeTaxFreeWithdrawn,
    taxFreeLumpSumCap,
    calculateTax,
  });
  const dbPensionThisYear = dbPensionGrossForAge({ age, dbPensionAge, dbPensionAnnual, lifeExpectancy });
  const countsTowardNetTarget = includeStatePensionInWithdrawal || dbPensionThisYear > 0;
  return countsTowardNetTarget ? finances.recurringNet : finances.potOnlyNet;
}
